import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { OBJECTIVES, BUDGET, FUNDERS, TEAM, TIMELINE } from "../lib/content";

const rise = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.7 } };

function Accordion({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
      <button data-testid={testid} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <span className="font-display font-bold text-sm uppercase tracking-wide">{title}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

export function Plan() {
  return (
    <section id="plan" className="bg-[#F5F2EC] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...rise} className="mono-label text-[#C2410C] mb-4">Break the Cycle — Christchurch pilot</motion.p>
        <motion.h2 {...rise} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-4">The 12-month plan</motion.h2>
        <motion.p {...rise} className="text-[#52525B] max-w-3xl mb-12">
          A community prevention and early-intervention initiative combining community education, lived-experience participation, whānau information, youth prevention, community hui and connections to existing professional support services — aligned with the national 2026–2029 Action Plan to Prevent and Reduce Substance Harm. Requested: <strong>NZ$28,000</strong>.
        </motion.p>

        <div className="grid sm:grid-cols-2 gap-3 mb-12">
          {OBJECTIVES.map((o, i) => (
            <motion.div key={o} {...rise} transition={{ delay: (i % 2) * 0.06, duration: 0.5 }} className="flex gap-4 items-start">
              <span className="mono-label text-[#C2410C] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-sm text-[#1A1A1A]">{o}</p>
            </motion.div>
          ))}
        </div>

        <div className="space-y-3 max-w-4xl">
          <Accordion title="Proposed first-year budget — $28,000" defaultOpen testid="plan-budget-accordion">
            <table className="w-full text-sm">
              <tbody>
                {BUDGET.map(([cat, amt]) => (
                  <tr key={cat} className="border-b border-[#E2DCD2]">
                    <td className="py-2 text-[#52525B]">{cat}</td>
                    <td className="py-2 text-right font-grotesk font-bold">{amt}</td>
                  </tr>
                ))}
                <tr className="font-extrabold"><td className="py-2">Total</td><td className="py-2 text-right font-grotesk">$28,000</td></tr>
              </tbody>
            </table>
            <p className="text-xs text-[#52525B] mt-3">A planning budget, adjusted to each funder's requirements.</p>
          </Accordion>
          <Accordion title="Evaluation & sustainability" testid="plan-evaluation-accordion">
            <p className="text-sm text-[#52525B] leading-relaxed">We will measure: people reached, educational resources distributed, events delivered, community organisations involved, volunteers recruited, people seeking information, referrals and navigation connections, participant feedback, lived-experience participation, and social-media and website reach. Year one establishes governance, partnerships, credibility, evaluation systems and an evidence base — then we grow.</p>
          </Accordion>
          <Accordion title="Safeguarding policies" testid="plan-safeguarding-accordion">
            <p className="text-sm text-[#52525B] leading-relaxed">The campaign operates under policies covering participant safety, child protection, privacy, informed consent, lived-experience storytelling, complaints, conflicts of interest, volunteer conduct and media engagement.</p>
          </Accordion>
          <Accordion title="Where we're seeking funding" testid="plan-funders-accordion">
            <div className="grid sm:grid-cols-2 gap-3">
              {FUNDERS.map(([name, desc, note]) => (
                <div key={name} className="border border-[#E2DCD2] rounded-lg p-4">
                  <h5 className="font-bold text-sm mb-1">{name}</h5>
                  <p className="text-xs text-[#52525B] mb-2">{desc}</p>
                  <span className="mono-label text-[#C2410C] text-[9px]">{note}</span>
                </div>
              ))}
            </div>
          </Accordion>
        </div>
      </div>
    </section>
  );
}

export function Team() {
  return (
    <section id="team" className="bg-[#F5F2EC] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...rise} className="mono-label text-[#C2410C] mb-4">Founding team</motion.p>
        <motion.h2 {...rise} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-4">Who we need</motion.h2>
        <motion.p {...rise} className="text-[#52525B] max-w-xl mb-12">Initially advisory, volunteer or part-time roles — not eight paid employees. Sound like you? <a href="#support" className="text-[#1E4B3E] font-bold underline">Get in touch.</a></motion.p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEAM.map(([tag, role, desc], i) => (
            <motion.div key={role} {...rise} transition={{ delay: (i % 4) * 0.07, duration: 0.5 }} className="bg-white border border-[#E2DCD2] rounded-xl p-5 hover:border-[#1E4B3E] transition-colors">
              <span className="mono-label text-[#C2410C] text-[9px]">{tag}</span>
              <h4 className="font-display font-bold text-sm mt-2 mb-1">{role}</h4>
              <p className="text-xs text-[#52525B]">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Launch() {
  return (
    <section id="launch" className="bg-[#EFEAE0] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...rise} className="mono-label text-[#C2410C] mb-4">The Christchurch launch</motion.p>
        <motion.h2 {...rise} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-4">Launch day</motion.h2>
        <motion.p {...rise} className="text-[#52525B] max-w-xl mb-12">Not a protest. Not simply a speech. A community prevention and recovery event — with zones for education, whānau resources, recovery storytelling, professional services, youth, and a respectful Memory Wall.</motion.p>
        <div className="relative border-l-2 border-[#1E4B3E]/30 ml-2 space-y-6">
          {TIMELINE.map(([time, title, desc], i) => (
            <motion.div key={time} {...rise} transition={{ delay: i * 0.04, duration: 0.5 }} className="relative pl-8">
              <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#1E4B3E]" />
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-grotesk font-bold text-[#1E4B3E] text-sm w-12">{time}</span>
                <h4 className="font-display font-bold text-sm uppercase tracking-wide">{title}</h4>
              </div>
              {desc && <p className="text-xs text-[#52525B] mt-1 ml-16 max-w-xl">{desc}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
